import {
  CONVICTION_SCALE_PPB,
  checkedBondingCurvePostTradeState,
  deriveBondingCurvePoolOutcomeReserveD6,
  isPositiveU64,
  isU64,
  MAX_EVENT_CONVICTION_PPB,
  reserveRatioSpotPriceD9,
} from '@morfi/market-math';
import type { MarketPoolRealtimeSnapshot } from './market-pool-realtime-snapshot.js';
import { projectMarketPoolResolutionAt } from './market-pool-resolution.js';

export type PoolRealtimePatchFailureReason =
  | 'identity_mismatch'
  | 'lifecycle_mismatch'
  | 'missing_pool_state'
  | 'malformed_event'
  | 'non_monotonic_event'
  | 'incoherent_post_state';

export type MarketPoolRealtimePatchResult =
  | { readonly ok: true; readonly snapshot: MarketPoolRealtimeSnapshot }
  | { readonly ok: false; readonly reason: PoolRealtimePatchFailureReason };

export interface BondingCurveTradeSnapshotPatch {
  market: string;
  outcomeIndex: number;
  outcomeMint: string;
  direction: 'buy' | 'sell';
  chainSlot: bigint;
  chainUnixTimestampSeconds: bigint;
  postPoolQuoteReserveD6: bigint;
  postOutstandingOutcomeSupplyD6: bigint;
  postOutcomeConvictionPpb: readonly bigint[];
  postConvictionRetentionPerSecondPpb: bigint;
  postLastConvictionUpdateUnixSeconds: bigint;
}

export interface CpPoolStateSnapshotPatch {
  pool: string;
  outcomeMint: string;
  chainSlot: bigint;
  chainUnixTimestampSeconds: bigint;
  postQuoteReserveD6: bigint;
  postOutcomeReserveD6: bigint;
  postOutcomePriceD9: bigint;
}

export function applyBondingCurveTradeToMarketPoolSnapshot(
  snapshot: MarketPoolRealtimeSnapshot,
  patch: BondingCurveTradeSnapshotPatch,
): MarketPoolRealtimePatchResult {
  if (
    snapshot.market !== patch.market ||
    !Number.isSafeInteger(patch.outcomeIndex) ||
    patch.outcomeIndex < 0
  ) {
    return unavailable('identity_mismatch');
  }
  if (snapshot.lifecycle !== 'trading') return unavailable('lifecycle_mismatch');
  const gate = snapshot.bondingCurveTradeGate;
  const outcomePosition = snapshot.outcomes.findIndex(
    ({ outcomeIndex }) => outcomeIndex === patch.outcomeIndex,
  );
  const outcome = snapshot.outcomes[outcomePosition];
  const curve = outcome?.bondingCurve;
  if (
    gate === null ||
    curve === null ||
    curve === undefined ||
    outcome === undefined ||
    outcomePosition < 0
  ) {
    return unavailable('missing_pool_state');
  }
  if (outcome.outcomeMint !== patch.outcomeMint) return unavailable('identity_mismatch');

  const currentSlot = unsignedWire(snapshot.chainSlot);
  const currentUnixTimestamp = unsignedWire(snapshot.chainUnixTimestampSeconds);
  const virtualQuoteReserveD6 = unsignedWire(curve.virtualQuoteReserveD6);
  const currentPoolQuoteReserveD6 = unsignedWire(curve.poolQuoteReserveD6);
  const currentPoolOutcomeReserveD6 = unsignedWire(curve.poolOutcomeReserveD6);
  const initialOutcomeSupplyD6 = unsignedWire(curve.initialOutcomeSupplyD6);
  const currentOutstandingSupplyD6 = unsignedWire(curve.trackedBondingCurveSoldSupplyD6);
  const currentLastConvictionUpdate = unsignedWire(gate.lastConvictionUpdateUnixSeconds);
  if (
    currentSlot === null ||
    currentUnixTimestamp === null ||
    virtualQuoteReserveD6 === null ||
    currentPoolQuoteReserveD6 === null ||
    currentPoolOutcomeReserveD6 === null ||
    initialOutcomeSupplyD6 === null ||
    currentOutstandingSupplyD6 === null ||
    currentLastConvictionUpdate === null ||
    !isU64(patch.chainSlot) ||
    !isU64(patch.chainUnixTimestampSeconds) ||
    !isPositiveU64(patch.postPoolQuoteReserveD6) ||
    !isU64(patch.postOutstandingOutcomeSupplyD6) ||
    patch.postConvictionRetentionPerSecondPpb < 0n ||
    patch.postConvictionRetentionPerSecondPpb >= CONVICTION_SCALE_PPB ||
    !isU64(patch.postLastConvictionUpdateUnixSeconds) ||
    patch.postOutcomeConvictionPpb.length !== snapshot.outcomes.length ||
    patch.postOutcomeConvictionPpb.some(
      (value) => !isU64(value) || value > MAX_EVENT_CONVICTION_PPB,
    )
  ) {
    return unavailable('malformed_event');
  }
  if (
    patch.chainSlot < currentSlot ||
    patch.postLastConvictionUpdateUnixSeconds < currentLastConvictionUpdate ||
    !movesInDirection(
      patch.direction,
      currentPoolQuoteReserveD6,
      patch.postPoolQuoteReserveD6,
      currentOutstandingSupplyD6,
      patch.postOutstandingOutcomeSupplyD6,
    )
  ) {
    return unavailable('non_monotonic_event');
  }

  const postPoolOutcomeReserve = deriveBondingCurvePoolOutcomeReserveD6(
    initialOutcomeSupplyD6,
    patch.postOutstandingOutcomeSupplyD6,
  );
  if (!postPoolOutcomeReserve.available || patch.postPoolQuoteReserveD6 < virtualQuoteReserveD6) {
    return unavailable('incoherent_post_state');
  }
  const nextState = checkedBondingCurvePostTradeState({
    state: {
      virtualQuoteReserveD6,
      poolQuoteReserveD6: currentPoolQuoteReserveD6,
      initialOutcomeSupplyD6,
      poolOutcomeReserveD6: currentPoolOutcomeReserveD6,
    },
    direction: patch.direction,
    postPoolQuoteReserveD6: patch.postPoolQuoteReserveD6,
    postPoolOutcomeReserveD6: postPoolOutcomeReserve.value,
  });
  if (!nextState.available) {
    return unavailable('incoherent_post_state');
  }

  const outcomes = snapshot.outcomes.slice();
  outcomes[outcomePosition] = {
    ...outcome,
    realQuoteReserveD6: (patch.postPoolQuoteReserveD6 - virtualQuoteReserveD6).toString(),
    bondingCurve: {
      ...curve,
      poolQuoteReserveD6: patch.postPoolQuoteReserveD6.toString(),
      poolOutcomeReserveD6: postPoolOutcomeReserve.value.toString(),
      trackedBondingCurveSoldSupplyD6: patch.postOutstandingOutcomeSupplyD6.toString(),
    },
  };

  const updated: MarketPoolRealtimeSnapshot = {
    ...snapshot,
    chainSlot: patch.chainSlot.toString(),
    chainUnixTimestampSeconds: maximum(
      currentUnixTimestamp,
      patch.chainUnixTimestampSeconds,
    ).toString(),
    bondingCurveTradeGate: {
      ...gate,
      outcomeConvictionPpb: patch.postOutcomeConvictionPpb.map(String),
      lastConvictionUpdateUnixSeconds: patch.postLastConvictionUpdateUnixSeconds.toString(),
      convictionRetentionPerSecondPpb: patch.postConvictionRetentionPerSecondPpb.toString(),
    },
    outcomes,
  };
  if (
    updated.bondingCurveTradeGate?.tradingStartedUnixSeconds !== undefined &&
    updated.bondingCurveTradeGate.timedTradingWindow === undefined
  ) {
    try {
      updated.bondingCurveTradeGate.resolutionAtUnixSeconds =
        projectMarketPoolResolutionAt(updated);
    } catch {
      return unavailable('incoherent_post_state');
    }
  }
  return {
    ok: true,
    snapshot: updated,
  };
}

export function applyCpPoolStateToMarketPoolSnapshot(
  snapshot: MarketPoolRealtimeSnapshot,
  patch: CpPoolStateSnapshotPatch,
): MarketPoolRealtimePatchResult {
  if (snapshot.lifecycle !== 'graduated') return unavailable('lifecycle_mismatch');
  const outcomePosition = snapshot.outcomes.findIndex(
    ({ outcomeMint, cpSwap }) => outcomeMint === patch.outcomeMint && cpSwap?.pool === patch.pool,
  );
  const outcome = snapshot.outcomes[outcomePosition];
  const pool = outcome?.cpSwap;
  if (outcome === undefined || pool === null || pool === undefined || outcomePosition < 0) {
    return unavailable('identity_mismatch');
  }
  const currentSlot = unsignedWire(snapshot.chainSlot);
  const currentUnixTimestamp = unsignedWire(snapshot.chainUnixTimestampSeconds);
  if (
    currentSlot === null ||
    currentUnixTimestamp === null ||
    !isU64(patch.chainSlot) ||
    !isU64(patch.chainUnixTimestampSeconds) ||
    !isPositiveU64(patch.postQuoteReserveD6) ||
    !isPositiveU64(patch.postOutcomeReserveD6) ||
    !isPositiveU64(patch.postOutcomePriceD9)
  ) {
    return unavailable('malformed_event');
  }
  if (patch.chainSlot < currentSlot) return unavailable('non_monotonic_event');
  const exactPrice = reserveRatioSpotPriceD9(patch.postQuoteReserveD6, patch.postOutcomeReserveD6);
  if (!exactPrice.available || exactPrice.value !== patch.postOutcomePriceD9) {
    return unavailable('incoherent_post_state');
  }
  const outcomes = snapshot.outcomes.slice();
  outcomes[outcomePosition] = {
    ...outcome,
    cpSwap: {
      ...pool,
      quoteReserveD6: patch.postQuoteReserveD6.toString(),
      outcomeReserveD6: patch.postOutcomeReserveD6.toString(),
    },
  };
  return {
    ok: true,
    snapshot: {
      ...snapshot,
      chainSlot: patch.chainSlot.toString(),
      chainUnixTimestampSeconds: maximum(
        currentUnixTimestamp,
        patch.chainUnixTimestampSeconds,
      ).toString(),
      outcomes,
    },
  };
}

function movesInDirection(
  direction: 'buy' | 'sell',
  currentQuoteReserveD6: bigint,
  postQuoteReserveD6: bigint,
  currentOutstandingSupplyD6: bigint,
  postOutstandingSupplyD6: bigint,
): boolean {
  return direction === 'buy'
    ? postQuoteReserveD6 > currentQuoteReserveD6 &&
        postOutstandingSupplyD6 > currentOutstandingSupplyD6
    : postQuoteReserveD6 < currentQuoteReserveD6 &&
        postOutstandingSupplyD6 < currentOutstandingSupplyD6;
}

function unsignedWire(value: string): bigint | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isU64(parsed) ? parsed : null;
}

function maximum(left: bigint, right: bigint): bigint {
  return left > right ? left : right;
}

function unavailable(reason: PoolRealtimePatchFailureReason): MarketPoolRealtimePatchResult {
  return { ok: false, reason };
}
